import { v4 } from "uuid";
import { addAuthzMetadataGrpcCall, buildGrpcClient } from "../../../../../lib/grpc";
import { ExperimentRepository } from "../../domain/interfaces/ExperimentRepository";
import {
  ExperimentCreateInput,
  ExperimentEntity,
  ExperimentFilters,
  ExperimentListItem,
  ExperimentResults,
  ExperimentStatus,
} from "../../domain/experiment.entity";
import { ExperimentMapper } from "../mappers/ExperimentMapper";
import { ExperimentAPIPromiseClient } from "../../../../../gen/witbooking/backoffice/orchestrator/experiment/v1/experiment_api_grpc_web_pb";
import {
  ChangeExperimentStatusRequest,
  CreateExperimentRequest,
  DeleteExperimentRequest,
  ExportExperimentMetricsRequest,
  GetExperimentRequest,
  GetExperimentResultsRequest,
  ListExperimentsRequest,
} from "../../../../../gen/witbooking/backoffice/orchestrator/experiment/v1/experiment_api_pb";

export class ExperimentGrpcRepository implements ExperimentRepository {
  private readonly client: ExperimentAPIPromiseClient;

  constructor() {
    this.client = buildGrpcClient(ExperimentAPIPromiseClient);
  }

  async list(filters: ExperimentFilters): Promise<ExperimentListItem[]> {
    const request = new ListExperimentsRequest();
    request.setTickerContains(filters.tickerContains ?? "");
    request.setStatus(filters.status ?? ExperimentStatus.EXPERIMENT_STATUS_UNSPECIFIED);
    request.setStartedAtFrom(filters.startedAtFrom ? filters.startedAtFrom.getTime() : 0);
    request.setStartedAtTo(filters.startedAtTo ? filters.startedAtTo.getTime() : 0);
    request.setMinDurationDays(filters.minDurationDays ?? 0);

    const call = addAuthzMetadataGrpcCall(this.client, this.client.listExperiments);
    const response = await call(request);

    return response.getItemsList().map(item => ExperimentMapper.toListItemDomain(item));
  }

  async findById(id: string): Promise<ExperimentEntity | null> {
    const request = new GetExperimentRequest();
    request.setId(id);

    const call = addAuthzMetadataGrpcCall(this.client, this.client.getExperiment);
    try {
      const response = await call(request);
      const experiment = response.getExperiment();
      return experiment ? ExperimentMapper.toDomain(experiment) : null;
    } catch (error) {
      if (error && typeof error === "object" && "code" in error && error.code === 5) {
        return null; // gRPC NOT_FOUND
      }
      throw error;
    }
  }

  async create(data: ExperimentCreateInput): Promise<ExperimentEntity> {
    const id = v4();
    const request = new CreateExperimentRequest();
    request.setExperiment(ExperimentMapper.createInputToProto(id, data));

    const call = addAuthzMetadataGrpcCall(this.client, this.client.createExperiment);
    const response = await call(request);

    const experiment = response.getExperiment();
    if (!experiment) {
      throw new Error("Unexpected response creating Experiment");
    }
    return ExperimentMapper.toDomain(experiment);
  }

  async changeStatus(id: string, status: ExperimentStatus): Promise<ExperimentEntity> {
    const request = new ChangeExperimentStatusRequest();
    request.setExperimentId(id);
    request.setNewStatus(status);

    const call = addAuthzMetadataGrpcCall(this.client, this.client.changeExperimentStatus);
    const response = await call(request);

    const experiment = response.getExperiment();
    if (!experiment) {
      throw new Error("Unexpected response changing Experiment status");
    }
    return ExperimentMapper.toDomain(experiment);
  }

  async softDelete(id: string): Promise<void> {
    const request = new DeleteExperimentRequest();
    request.setId(id);

    const call = addAuthzMetadataGrpcCall(this.client, this.client.deleteExperiment);
    await call(request);
  }

  async results(id: string, from: number, to: number): Promise<ExperimentResults> {
    const request = new GetExperimentResultsRequest();
    request.setExperimentId(id);
    request.setFrom(from);
    request.setTo(to);

    const call = addAuthzMetadataGrpcCall(this.client, this.client.getExperimentResults);
    const response = await call(request);

    return ExperimentMapper.toResultsDomain(response);
  }

  async exportMetricsCsv(id: string): Promise<string> {
    const request = new ExportExperimentMetricsRequest();
    request.setExperimentId(id);

    const call = addAuthzMetadataGrpcCall(this.client, this.client.exportExperimentMetrics);
    const response = await call(request);

    return response.getCsv();
  }
}
